/**
 * THE RENTALS SIDE: WHAT A TRIP IS, WHERE IT CAN GO, AND WHAT IT COSTS.
 *
 * Anthony, 2026-09-28, for WeRV, an RV rental yard in Hemet: "a working
 * version for rv rental on one side of their crm, rv repair on the other,
 * in one dash with a button to swap between the two". The repair side is
 * the eighteen step job. This is the other side: a trip, from the first
 * text to the deposit coming back, in nine lanes.
 *
 *   Inquiry, Quoted, Booked, Prep, Out, Returning, Inspection, Settled,
 *   Closed. And Cancelled, which is off to the side, not a lane.
 *
 * ─── PORTED FROM THE DEMO, NOT COPIED ──────────────────────────────────
 *
 * The werv demo repo already ran a rental back office (its
 * netlify/functions/_lib.mjs). What came across is its RULES: the lanes,
 * the price arithmetic, the delivery fee, the turnover day and the 25
 * percent down. Its plumbing (blobs, etags, a JSON fleet file) did not,
 * because here the database holds the calendar and an exclusion
 * constraint (migration 0185) does the job its etag retry loop did.
 *
 * ─── EVERY FIGURE IS A PLACEHOLDER POLICY ──────────────────────────────
 *
 * The numbers in RENTAL_POLICY are the demo's, which were written before
 * the yard confirmed any of them. They are named in one object so the day
 * a yard says "we charge $2 a mile" the change is one line here, and every
 * screen, the server's quote and the tests move together. A per-account
 * settings row is the obvious next step and deliberately not taken yet:
 * one rental yard is on the system, and a settings screen for one shop's
 * placeholder numbers is a screen for numbers nobody has confirmed.
 *
 * ─── PURE ──────────────────────────────────────────────────────────────
 *
 * No imports and no clock. Anything that needs "today" takes it as a
 * YYYY-MM-DD argument, because "today" is the SHOP's day (its own
 * timezone), and only the caller knows which shop it is. The browser and
 * the server both import this, so the price on the screen and the price
 * the server saves are the same function.
 */

/* ── the lanes ───────────────────────────────────────────────────────── */

export type Stage =
  | "inquiry" | "quoted" | "booked" | "prep" | "out" | "returning"
  | "inspection" | "settled" | "closed" | "cancelled";

export type Lane = {
  key: Stage;
  label: string;
  /** Who moves it on, for the process page. */
  who: "Desk" | "Yard" | "Renter" | "System";
  /** One short line: what this lane means. */
  line: string;
  /** The rule the system holds, if it holds one. */
  rule?: string;
};

/** THE NINE LANES, in board order. Cancelled is not one of them. */
export const LANES: readonly Lane[] = [
  { key: "inquiry", label: "Inquiry", who: "Desk",
    line: "Somebody asked. Dates, how many are going, where to, and whether it gets delivered.",
    rule: "A text to the shop lands here by itself, with the dates filled in when they read cleanly." },
  { key: "quoted", label: "Quoted", who: "Desk",
    line: "A unit and a price have gone to them. Nothing is held yet.",
    rule: "A quote holds nothing. Two families can be quoted the same weekend and the first to pay gets it." },
  { key: "booked", label: "Booked", who: "Renter",
    line: "They paid the down payment. The RV is theirs for those dates.",
    rule: "One RV cannot be booked twice for overlapping dates, with a turnover day each side. The database refuses it." },
  { key: "prep", label: "Prep", who: "Yard",
    line: "One or two days out. Clean, fill the propane and water, check the balance is paid." },
  { key: "out", label: "Out", who: "Yard",
    line: "Pre trip walk round done and signed. Odometer, generator hours and tanks written down.",
    rule: "Filing the pre trip walk round moves the trip here." },
  { key: "returning", label: "Returning", who: "System",
    line: "It is due back today. The board moves it here on the return day." },
  { key: "inspection", label: "Inspection", who: "Yard",
    line: "Post trip walk round. Miles and generator hours driven, tanks, damage.",
    rule: "Filing the post trip walk round moves the trip here." },
  { key: "settled", label: "Settled", who: "Desk",
    line: "The deposit hold is released, or part of it kept against logged damage with a note.",
    rule: "Settling the deposit moves the trip here." },
  { key: "closed", label: "Closed", who: "Desk",
    line: "Nothing is owed either way. The trip is history." },
];

export const STAGES: readonly Stage[] = [...LANES.map((l) => l.key), "cancelled"];

export const STAGE_LABEL: Record<Stage, string> = {
  ...Object.fromEntries(LANES.map((l) => [l.key, l.label])) as Record<Exclude<Stage, "cancelled">, string>,
  cancelled: "Cancelled",
};

export const isStage = (s: unknown): s is Stage => typeof s === "string" && (STAGES as readonly string[]).includes(s);

/**
 * THE STAGES WHERE THE RV IS SPOKEN FOR. Exactly the list in migration
 * 0185's exclusion constraint. A trip in one of these must have a unit and
 * both dates, and holds the RV against every other trip.
 */
export const HOLDING: readonly Stage[] = ["booked", "prep", "out", "returning", "inspection"];
export const holds = (s: Stage): boolean => HOLDING.includes(s);

/** Cancelling is allowed while the RV is still in the yard. */
const CANCELLABLE: readonly Stage[] = ["inquiry", "quoted", "booked", "prep"];

/**
 * WHERE A TRIP MAY GO FROM WHERE IT IS.
 *
 * Forward one lane, back one lane (somebody pressed the wrong button), or
 * Cancelled from anything before Out. Closed and Cancelled are the end: a
 * closed trip reopened is a new trip, and a cancelled one rebooked is a
 * new trip too, so its history is not rewritten under it.
 *
 * Back one lane is there for the wrong button, not for history: moving a
 * trip back does not unfile a walk round or unsettle a deposit, it only
 * puts the card where it belongs.
 */
export function mayMove(from: Stage, to: Stage): boolean {
  if (from === to) return false;
  if (from === "closed" || from === "cancelled") return false;
  if (to === "cancelled") return CANCELLABLE.includes(from);
  const lanes = LANES.map((l) => l.key);
  const a = lanes.indexOf(from);
  const b = lanes.indexOf(to);
  if (a < 0 || b < 0) return false;
  return b === a + 1 || b === a - 1;
}

/** Every stage this one may move to, board order. */
export const movesFrom = (from: Stage): Stage[] => STAGES.filter((to) => mayMove(from, to));

/* ── dates, as YYYY-MM-DD, zone free ─────────────────────────────────── */

const YMD = /^\d{4}-\d{2}-\d{2}$/;
export const isYmd = (s: unknown): s is string => typeof s === "string" && YMD.test(s)
  && !Number.isNaN(Date.parse(`${s}T12:00:00Z`)) && new Date(`${s}T12:00:00Z`).toISOString().slice(0, 10) === s;

const at = (d: string): number => Date.parse(`${d}T12:00:00Z`);

export function addDays(d: string, n: number): string {
  return new Date(at(d) + n * 86_400_000).toISOString().slice(0, 10);
}

/** Nights from pickup to return. 0 when either is missing or they are out of order. */
export function nightsBetween(start: string | null | undefined, end: string | null | undefined): number {
  if (!isYmd(start) || !isYmd(end)) return 0;
  return Math.max(0, Math.round((at(end) - at(start)) / 86_400_000));
}

/** Whole days from `today` to `d`. Negative when `d` has passed. */
export const daysUntil = (today: string, d: string): number => Math.round((at(d) - at(today)) / 86_400_000);

/** 0 Sunday to 6 Saturday. */
const weekday = (d: string): number => new Date(at(d)).getUTCDay();

/** "Oct 14", for a card. */
export function shortDay(d: string | null | undefined): string {
  if (!isYmd(d)) return "";
  return new Date(at(d)).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/* ── the lane a trip SHOULD be in today ──────────────────────────────── */

/**
 * WHAT THE CALENDAR SAYS, as opposed to what somebody last pressed.
 *
 * The demo moved these by itself; here the board SUGGESTS and a person
 * presses, because a trip moved to Prep by a clock while the renter has
 * not paid is a clean nobody should do. Two rules, the demo's:
 *
 *   Booked becomes Prep from two days before pickup (PREP_DAYS_BEFORE).
 *   Out becomes Returning on the return day or after it.
 *
 * Null when it is where it should be.
 */
export function laneDue(stage: Stage, start: string | null, end: string | null, today: string): Stage | null {
  if (stage === "booked" && isYmd(start) && daysUntil(today, start) <= RENTAL_POLICY.prepDaysBefore) return "prep";
  if (stage === "out" && isYmd(end) && daysUntil(today, end) <= 0) return "returning";
  return null;
}

/** Still out after the return day. */
export const isOverdue = (stage: Stage, end: string | null, today: string): boolean =>
  (stage === "out" || stage === "returning") && isYmd(end) && daysUntil(today, end) < 0;

/** "back Oct 14", "due back today", or "overdue 2 days", for a pin or a card. */
export function backLine(stage: Stage, end: string | null, today: string): string {
  if (!isYmd(end)) return "no return day yet";
  const d = daysUntil(today, end);
  if ((stage === "out" || stage === "returning") && d < 0) return `overdue ${-d} day${d === -1 ? "" : "s"}`;
  if (d === 0) return "due back today";
  if (d === 1) return "back tomorrow";
  return `back ${shortDay(end)}`;
}

/* ── the policy, in one place ────────────────────────────────────────── */

/**
 * THE DEMO'S PLACEHOLDER POLICY. Every figure below came from the werv
 * demo's DEFAULT_SETTINGS and has not been confirmed by a yard. Change
 * them here and only here: the New trip form, the trip page and the
 * server's saved quote all read this object.
 */
export const RENTAL_POLICY = {
  /** Fri and Sat nights cost this much more. 0.1 is ten percent. */
  weekendUplift: 0.1,
  /** Off the nights for a stay this long or longer. */
  weekNights: 7,
  weekDiscount: 0.1,
  monthNights: 28,
  monthDiscount: 0.2,
  /** Delivery: the first miles each way are free. */
  deliveryFreeMiles: 15,
  /** Then this much a mile, charged for the round trip. */
  deliveryPerMile: 1.75,
  /** Never less than this once any mile is billed. */
  deliveryMinimum: 85,
  /** Rounded to the nearest this. */
  deliveryRoundTo: 5,
  /** Setup and teardown at the site, whenever it is delivered. */
  setupFee: 125,
  /** Down payment to book, as a share of the total. */
  downShare: 0.25,
  /** The balance is due this many days before pickup. */
  balanceDueDaysBefore: 7,
  /** The board suggests Prep this many days before pickup. */
  prepDaysBefore: 2,
  /** A unit with no deposit set holds this much. */
  defaultDeposit: 500,
} as const;

/** Cents, rounded half away from zero, back to dollars. */
export function money2(n: number): number {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.sign(x) * Math.round((Math.abs(x) + Number.EPSILON) * 100) / 100;
}

/* ── the quote ───────────────────────────────────────────────────────── */

export type QuoteInput = {
  nightly: number | null | undefined;
  start: string | null | undefined;
  end: string | null | undefined;
  prepFee?: number | null;
  delivery?: boolean;
  /** One way, as staff enter it. */
  deliveryMiles?: number | null;
};

export type QuoteLine = { code: "nights" | "weekend" | "discount" | "prep" | "delivery" | "setup"; label: string; amount: number };

export type Quote = {
  nights: number;
  lines: QuoteLine[];
  /** What the yard keeps whoever owns the unit: prep, delivery, setup. */
  fees: number;
  total: number;
  down: number;
  balance: number;
  /** YYYY-MM-DD, or null with no pickup day. */
  balanceDue: string | null;
};

/** The delivery charge for a one way distance, before setup. */
export function deliveryFee(miles: number | null | undefined): number {
  const m = Math.max(0, Number(miles) || 0);
  const billable = Math.max(0, m - RENTAL_POLICY.deliveryFreeMiles);
  if (!billable) return 0;
  const raw = billable * RENTAL_POLICY.deliveryPerMile * 2;
  const rounded = Math.round(raw / RENTAL_POLICY.deliveryRoundTo) * RENTAL_POLICY.deliveryRoundTo;
  return Math.max(RENTAL_POLICY.deliveryMinimum, rounded);
}

/**
 * THE PRICE OF A TRIP.
 *
 * Nightly times nights, with Friday and Saturday nights marked up; then
 * the week or month discount off the nights (the bigger one only, never
 * both); then the prep fee; then delivery and setup if it is delivered.
 * No tax: the demo's own note says a rental tax is the yard's accountant's
 * to set, and a guessed tax on a customer's quote is a number nobody
 * approved.
 */
export function quoteTrip(q: QuoteInput): Quote {
  const nights = nightsBetween(q.start, q.end);
  const rate = Math.max(0, Number(q.nightly) || 0);
  const lines: QuoteLine[] = [];
  let weekendNights = 0;
  for (let i = 0; i < nights; i++) {
    const w = weekday(addDays(q.start as string, i));
    if (w === 5 || w === 6) weekendNights++;
  }
  const base = money2(rate * nights);
  if (nights) lines.push({ code: "nights", label: `${nights} night${nights === 1 ? "" : "s"} at $${Number.isInteger(rate) ? rate : Number(rate).toFixed(2)}`, amount: base });
  const uplift = money2(rate * weekendNights * RENTAL_POLICY.weekendUplift);
  if (uplift) lines.push({ code: "weekend", label: `Friday and Saturday nights (${weekendNights})`, amount: uplift });
  const nightsTotal = base + uplift;
  const off = nights >= RENTAL_POLICY.monthNights ? RENTAL_POLICY.monthDiscount
    : nights >= RENTAL_POLICY.weekNights ? RENTAL_POLICY.weekDiscount : 0;
  const discount = money2(nightsTotal * off);
  if (discount) {
    lines.push({ code: "discount", label: nights >= RENTAL_POLICY.monthNights ? "Month long stay" : "Week long stay",
      amount: -discount });
  }
  const prep = money2(Math.max(0, Number(q.prepFee) || 0));
  if (prep && nights) lines.push({ code: "prep", label: "Prep, clean and propane", amount: prep });
  let fees = nights ? prep : 0;
  if (q.delivery && nights) {
    const d = deliveryFee(q.deliveryMiles);
    if (d) lines.push({ code: "delivery", label: `Delivery, ${Number(q.deliveryMiles) || 0} miles each way`, amount: d });
    lines.push({ code: "setup", label: "Setup and teardown at the site", amount: RENTAL_POLICY.setupFee });
    fees += d + RENTAL_POLICY.setupFee;
  }
  const total = money2(lines.reduce((n, l) => n + l.amount, 0));
  const down = money2(total * RENTAL_POLICY.downShare);
  return {
    nights, lines, fees: money2(fees), total, down, balance: money2(total - down),
    balanceDue: isYmd(q.start) ? addDays(q.start, -RENTAL_POLICY.balanceDueDaysBefore) : null,
  };
}

/**
 * THE FLEET OWNER'S SHARE of a trip on a unit in the partner program.
 * The fees (prep, delivery, setup) are the yard's work and stay with the
 * yard; the owner is paid their percent of the rest. Null when the unit
 * has no owner or no percent.
 */
export function ownerShare(total: number, fees: number, pct: number | null | undefined): number | null {
  const p = Number(pct);
  if (pct === null || pct === undefined || !Number.isFinite(p) || p <= 0) return null;
  return money2(Math.max(0, total - fees) * Math.min(100, p) / 100);
}

/** Balance still owed on a trip, never negative. */
export const owing = (total: number | null | undefined, paid: number | null | undefined): number =>
  money2(Math.max(0, (Number(total) || 0) - (Number(paid) || 0)));

/** The balance is due (or past due) and not paid, on a trip that is going. */
export function balanceUrgent(t: { stage: Stage; start_date: string | null; quoted_total: number | null; paid_amount: number | null },
  today: string): boolean {
  if (!["quoted", "booked", "prep"].includes(t.stage) || !isYmd(t.start_date)) return false;
  if (owing(t.quoted_total, t.paid_amount) <= 0) return false;
  return daysUntil(today, addDays(t.start_date, -RENTAL_POLICY.balanceDueDaysBefore)) <= 0;
}

/* ── one RV, one trip at a time ──────────────────────────────────────── */

/**
 * THE DAYS A TRIP TAKES THE RV OFF THE LOT, as a half open [from, to).
 *
 * THE MIRROR OF MIGRATION 0185's EXCLUSION CONSTRAINT and it must stay
 * exactly that: daterange(start_date - 1, end_date + 1), '[)' bounds.
 * From the turnover day before pickup up to and including the return day.
 * The next pickup can be two days after a return at the earliest, with the
 * day between as the one turnover day. The server never trusts this to
 * decide (the constraint does), it uses it to say so before the insert
 * and to shade the calendar.
 */
export function blocksOf(start: string, end: string): { from: string; to: string } {
  return { from: addDays(start, -1), to: addDays(end, 1) };
}

export type Hold = { id?: string | null; vehicle_id: string | null; stage: Stage; start_date: string | null; end_date: string | null };

/** Whether two trips would break the constraint. Same rule, same WHERE clause. */
export function clash(a: Hold, b: Hold): boolean {
  if (a.id && b.id && a.id === b.id) return false;
  if (!a.vehicle_id || !b.vehicle_id || a.vehicle_id !== b.vehicle_id) return false;
  if (!holds(a.stage) || !holds(b.stage)) return false;
  if (!isYmd(a.start_date) || !isYmd(a.end_date) || !isYmd(b.start_date) || !isYmd(b.end_date)) return false;
  const x = blocksOf(a.start_date, a.end_date);
  const y = blocksOf(b.start_date, b.end_date);
  return x.from < y.to && y.from < x.to;
}

/** The first trip in `others` this one would clash with, or null. */
export const clashWith = (t: Hold, others: readonly Hold[]): Hold | null => others.find((o) => clash(t, o)) ?? null;

/* ── what staff send, checked ────────────────────────────────────────── */

const text = (v: unknown, max: number): string | null => {
  const s = String(v ?? "").replace(/\s+/g, " ").trim().slice(0, max);
  return s || null;
};
const whole = (v: unknown, lo: number, hi: number): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= lo && n <= hi ? n : NaN;
};
const amount = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n < 1_000_000 ? money2(n) : NaN;
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (s: unknown): s is string => typeof s === "string" && UUID.test(s);

/** The fields staff may set on a trip, and nothing else. */
export const TRIP_FIELDS = [
  "vehicle_id", "customer_id", "renter_name", "renter_phone", "renter_email", "start_date", "end_date",
  "party", "destination", "destination_lat", "destination_lng", "delivery", "delivery_address", "delivery_miles",
  "pets", "source", "nightly_rate", "quoted_total", "down_payment", "paid_amount", "deposit_amount", "damage_notes", "notes",
] as const;
export type TripField = (typeof TRIP_FIELDS)[number];

/**
 * A partial trip from whatever the browser sent. Only named fields, each
 * cleaned, and a sentence naming the first one that is wrong. A key that
 * is absent is left alone; a key sent as "" or null clears it.
 */
export function parseTripFields(input: unknown): { ok: true; value: Partial<Record<TripField, unknown>> } | { ok: false; error: string } {
  if (!input || typeof input !== "object") return { ok: false, error: "Nothing was sent." };
  const b = input as Record<string, unknown>;
  const v: Partial<Record<TripField, unknown>> = {};
  const has = (k: string) => Object.prototype.hasOwnProperty.call(b, k);
  for (const k of ["vehicle_id", "customer_id"] as const) {
    if (!has(k)) continue;
    if (b[k] === null || b[k] === "") v[k] = null;
    else if (isUuid(b[k])) v[k] = b[k];
    else return { ok: false, error: k === "vehicle_id" ? "Pick an RV from the list." : "Pick a customer from the list." };
  }
  if (has("renter_name")) v.renter_name = text(b["renter_name"], 120);
  if (has("renter_phone")) {
    const d = String(b["renter_phone"] ?? "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
    if (d && d.length !== 10) return { ok: false, error: "The phone number needs ten digits." };
    v.renter_phone = d || null;
  }
  if (has("renter_email")) {
    const e = text(b["renter_email"], 200);
    if (e && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return { ok: false, error: "That email does not look right." };
    v.renter_email = e;
  }
  for (const k of ["start_date", "end_date"] as const) {
    if (!has(k)) continue;
    if (b[k] === null || b[k] === "") v[k] = null;
    else if (isYmd(b[k])) v[k] = b[k];
    else return { ok: false, error: k === "start_date" ? "The pickup day is not a date." : "The return day is not a date." };
  }
  const ints: Array<[TripField, number, number, string]> = [
    ["party", 1, 30, "How many are going has to be 1 to 30."],
    ["pets", 0, 10, "Pets has to be 0 to 10."],
    ["delivery_miles", 0, 1000, "Delivery miles has to be a whole number."],
  ];
  for (const [k, lo, hi, msg] of ints) {
    if (!has(k)) continue;
    const n = whole(b[k], lo, hi);
    if (Number.isNaN(n)) return { ok: false, error: msg };
    v[k] = k === "pets" ? (n ?? 0) : n;
  }
  if (has("destination")) v.destination = text(b["destination"], 200);
  for (const [k, lim] of [["destination_lat", 90], ["destination_lng", 180]] as const) {
    if (!has(k)) continue;
    if (b[k] === null || b[k] === "") { v[k] = null; continue; }
    const n = Number(b[k]);
    if (!Number.isFinite(n) || Math.abs(n) > lim) return { ok: false, error: "That map point is not a real place." };
    v[k] = Math.round(n * 1e6) / 1e6;
  }
  if (has("delivery")) v.delivery = b["delivery"] === true;
  if (has("delivery_address")) v.delivery_address = text(b["delivery_address"], 300);
  if (has("source")) {
    const s = String(b["source"] ?? "");
    if (!["intake", "desk", "phone", "web"].includes(s)) return { ok: false, error: "Unknown source." };
    v.source = s;
  }
  for (const k of ["nightly_rate", "quoted_total", "down_payment", "paid_amount", "deposit_amount"] as const) {
    if (!has(k)) continue;
    const n = amount(b[k]);
    if (Number.isNaN(n)) return { ok: false, error: "A money figure has to be a number, 0 or more." };
    v[k] = k === "paid_amount" ? (n ?? 0) : n;
  }
  if (has("damage_notes")) v.damage_notes = text(b["damage_notes"], 2000);
  if (has("notes")) v.notes = text(b["notes"], 4000);
  if (isYmd(v.start_date) && isYmd(v.end_date) && v.start_date >= v.end_date) {
    return { ok: false, error: "The return day has to be after the pickup day." };
  }
  return { ok: true, value: v };
}

/**
 * WHAT A TRIP NEEDS BEFORE IT MAY HOLD AN RV. The same thing migration
 * 0185 demands of a holding stage, plus the unit, said as a sentence.
 */
export function readyToHold(t: { vehicle_id: string | null; start_date: string | null; end_date: string | null }): string | null {
  if (!t.vehicle_id) return "Pick which RV first.";
  if (!isYmd(t.start_date) || !isYmd(t.end_date)) return "Set the pickup and return days first.";
  if (t.start_date >= t.end_date) return "The return day has to be after the pickup day.";
  return null;
}

/* ── the walk round ──────────────────────────────────────────────────── */

export const TANK_LEVELS = ["Empty", "1/4", "1/2", "3/4", "Full"] as const;

/** The checklist every walk round asks, in the order a person walks it. */
export const CHECKLIST = [
  { key: "exterior", label: "Outside walked round, no new marks" },
  { key: "tires", label: "Tires and lug nuts" },
  { key: "awning", label: "Awning out and back" },
  { key: "slides", label: "Slides out and back" },
  { key: "generator", label: "Generator starts" },
  { key: "fridge", label: "Fridge cold" },
  { key: "water_heater", label: "Water heater" },
  { key: "ac", label: "Air conditioning" },
  { key: "lights", label: "Lights and signals" },
  { key: "interior", label: "Inside clean" },
] as const;

export type InspectionInput = {
  kind: "pre" | "post";
  odometer: number | null;
  generator_hours: number | null;
  fuel: string | null; propane: string | null;
  fresh: string | null; gray: string | null; black: string | null;
  checklist: Record<string, boolean>;
  notes: string | null;
  signed_name: string | null;
};

export function parseInspection(input: unknown): { ok: true; value: InspectionInput } | { ok: false; error: string } {
  if (!input || typeof input !== "object") return { ok: false, error: "Nothing was sent." };
  const b = input as Record<string, unknown>;
  const kind = b["kind"];
  if (kind !== "pre" && kind !== "post") return { ok: false, error: "Is this the pre trip or the post trip walk round?" };
  const odo = whole(b["odometer"], 0, 5_000_000);
  if (Number.isNaN(odo)) return { ok: false, error: "The odometer has to be a whole number." };
  const genRaw = b["generator_hours"];
  let gen: number | null = null;
  if (genRaw !== null && genRaw !== undefined && genRaw !== "") {
    gen = Number(genRaw);
    if (!Number.isFinite(gen) || gen < 0 || gen > 1_000_000) return { ok: false, error: "Generator hours has to be a number." };
    gen = Math.round(gen * 10) / 10;
  }
  const level = (k: string): string | null => {
    const s = text(b[k], 20);
    return s && (TANK_LEVELS as readonly string[]).includes(s) ? s : null;
  };
  const cl: Record<string, boolean> = {};
  const raw = (b["checklist"] && typeof b["checklist"] === "object") ? b["checklist"] as Record<string, unknown> : {};
  for (const c of CHECKLIST) if (raw[c.key] === true) cl[c.key] = true;
  const signed = text(b["signed_name"], 120);
  if (!signed) return { ok: false, error: "The renter signs it. Type their name as they signed." };
  return { ok: true, value: {
    kind, odometer: odo, generator_hours: gen,
    fuel: level("fuel"), propane: level("propane"), fresh: level("fresh"), gray: level("gray"), black: level("black"),
    checklist: cl, notes: text(b["notes"], 2000), signed_name: signed,
  } };
}

/** Miles and generator hours used between the two walk rounds, or null for each that cannot be said. */
export function used(pre: { odometer: number | null; generator_hours: number | null } | null,
  post: { odometer: number | null; generator_hours: number | null } | null): { miles: number | null; hours: number | null } {
  const diff = (a: number | null | undefined, b: number | null | undefined, dp: number): number | null =>
    a === null || a === undefined || b === null || b === undefined || b < a ? null : Math.round((b - a) * dp) / dp;
  return { miles: diff(pre?.odometer, post?.odometer, 1), hours: diff(pre?.generator_hours, post?.generator_hours, 10) };
}

/** The lane a walk round moves a trip from and to. */
export const INSPECTION_MOVES = { pre: { from: "prep", to: "out" }, post: { from: "returning", to: "inspection" } } as const;

/* ── the deposit ─────────────────────────────────────────────────────── */

export type Settle = { deposit_status: "released" | "kept_partial" | "kept_full"; deposit_kept: number; damage_notes: string | null };

/**
 * RELEASE IT, OR KEEP SOME OF IT, AND SAY WHY.
 *
 * Keeping any of a renter's deposit needs a sentence about the damage,
 * because it is the first thing they will ask and the one thing a person
 * will not remember in a month. Releasing needs nothing. Keeping more than
 * was held is refused, and keeping all of it is kept_full.
 */
export function parseSettle(input: unknown, held: number | null): { ok: true; value: Settle } | { ok: false; error: string } {
  if (!input || typeof input !== "object") return { ok: false, error: "Nothing was sent." };
  const b = input as Record<string, unknown>;
  const note = text(b["note"], 2000);
  if (b["action"] === "release") return { ok: true, value: { deposit_status: "released", deposit_kept: 0, damage_notes: note } };
  if (b["action"] !== "keep") return { ok: false, error: "Release it, or keep some of it." };
  const n = Number(b["amount"]);
  if (!Number.isFinite(n) || n <= 0) return { ok: false, error: "How much is kept?" };
  const max = Number(held) || 0;
  if (max <= 0) return { ok: false, error: "No deposit amount is on this trip. Set what was held first." };
  if (n > max) return { ok: false, error: `That is more than the $${max} held.` };
  if (!note) return { ok: false, error: "Say what the damage was. It is the first thing they will ask." };
  const kept = money2(n);
  return { ok: true, value: { deposit_status: kept >= max ? "kept_full" : "kept_partial", deposit_kept: kept, damage_notes: note } };
}

/* ── the unit's rental fields ────────────────────────────────────────── */

export const UNIT_FIELDS = ["rental_nightly_rate", "rental_deposit", "rental_prep_fee", "rental_min_nights",
  "rental_min_age", "rental_sleeps", "rental_active", "fleet_owner_name", "fleet_owner_payout_pct"] as const;
export type UnitField = (typeof UNIT_FIELDS)[number];

export function parseUnitFields(input: unknown): { ok: true; value: Partial<Record<UnitField, unknown>> } | { ok: false; error: string } {
  if (!input || typeof input !== "object") return { ok: false, error: "Nothing was sent." };
  const b = input as Record<string, unknown>;
  const has = (k: string) => Object.prototype.hasOwnProperty.call(b, k);
  const v: Partial<Record<UnitField, unknown>> = {};
  for (const k of ["rental_nightly_rate", "rental_deposit", "rental_prep_fee"] as const) {
    if (!has(k)) continue;
    const n = amount(b[k]);
    if (Number.isNaN(n)) return { ok: false, error: "A money figure has to be a number, 0 or more." };
    v[k] = n;
  }
  const ints: Array<[UnitField, number, number, string]> = [
    ["rental_min_nights", 1, 365, "Minimum nights has to be 1 to 365."],
    ["rental_min_age", 16, 99, "Minimum age has to be 16 to 99."],
    ["rental_sleeps", 1, 30, "Sleeps has to be 1 to 30."],
  ];
  for (const [k, lo, hi, msg] of ints) {
    if (!has(k)) continue;
    const n = whole(b[k], lo, hi);
    if (Number.isNaN(n)) return { ok: false, error: msg };
    v[k] = n;
  }
  if (has("rental_active")) v.rental_active = b["rental_active"] !== false;
  if (has("fleet_owner_name")) v.fleet_owner_name = text(b["fleet_owner_name"], 120);
  if (has("fleet_owner_payout_pct")) {
    const raw = b["fleet_owner_payout_pct"];
    if (raw === null || raw === "") v.fleet_owner_payout_pct = null;
    else {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || n > 100) return { ok: false, error: "The owner's share has to be 0 to 100 percent." };
      v.fleet_owner_payout_pct = Math.round(n * 100) / 100;
    }
  }
  return { ok: true, value: v };
}

/* ── the shapes the API hands the pages ──────────────────────────────── */

/** A fleet unit as the Rentals side reads it: a vehicles row with its rental fields. */
export type Unit = {
  id: string; year: number | null; make: string | null; model: string | null; trim: string | null;
  description: string | null; license_plate: string | null;
  rental_nightly_rate: number | null; rental_deposit: number | null; rental_prep_fee: number | null;
  rental_min_nights: number | null; rental_min_age: number | null; rental_sleeps: number | null;
  rental_active: boolean | null; fleet_owner_name: string | null; fleet_owner_payout_pct: number | null;
};

/** The columns every read of a unit asks for, one line, so the select string infers. */
export const UNIT_SELECT = "id,year,make,model,trim,description,license_plate,rental_nightly_rate,rental_deposit,rental_prep_fee,rental_min_nights,rental_min_age,rental_sleeps,rental_active,fleet_owner_name,fleet_owner_payout_pct";

/** "2021 Thor Four Winds 24F", or its description, or "An RV". */
export function unitName(u: Pick<Unit, "year" | "make" | "model" | "description"> | null | undefined): string {
  if (!u) return "";
  const ymm = [u.year, u.make, u.model].filter((x) => x !== null && x !== undefined && String(x).trim()).join(" ");
  return ymm || u.description?.trim() || "An RV";
}

export type Trip = {
  id: string; vehicle_id: string | null; customer_id: string | null;
  renter_name: string | null; renter_phone: string | null; renter_email: string | null;
  start_date: string | null; end_date: string | null; nights: number | null; stage: Stage;
  party: number | null; destination: string | null; destination_lat: number | null; destination_lng: number | null;
  delivery: boolean; delivery_address: string | null; delivery_miles: number | null; pets: number;
  source: string; intake_thread_id: string | null;
  nightly_rate: number | null; quoted_total: number | null; down_payment: number | null; paid_amount: number;
  deposit_amount: number | null; deposit_status: "none" | "held" | "released" | "kept_partial" | "kept_full";
  deposit_kept: number; damage_notes: string | null; notes: string | null; cancelled_reason: string | null;
  created_at: string; updated_at: string;
  unit: Unit | null;
};

export type Inspection = InspectionInput & { id: string; trip_id: string; filed_at: string; filed_by: string | null };

export const DEPOSIT_LABEL: Record<Trip["deposit_status"], string> = {
  none: "No hold yet", held: "Held", released: "Released", kept_partial: "Part kept", kept_full: "Kept in full",
};

/* ── demo trips, and putting a trip away ─────────────────────────────── */

/**
 * WHAT MAKES A TRIP A DEMO TRIP: ITS NOTES START WITH THIS LINE.
 *
 * Anthony, 2026-09-28: "Put one demo lead in every stage with an option to
 * delete them or archive them easily on the rental side when we're done
 * with the demo."
 *
 * No column, on purpose. The job had to ship with no database change, and
 * a marker the code owns does the one thing a flag would: it lets
 * tools/shops/rental-demo.mjs write them and the Remove demo trips button
 * take them away again, touching nothing a person typed. The marker is the
 * WHOLE first line, exactly, so a note that merely mentions "[demo]" in a
 * sentence, or a renter's own note beginning "[demo] of the slide", is
 * never mistaken for one. Somebody who edits a demo trip's notes and keeps
 * the first line keeps it a demo; somebody who deletes the line has
 * claimed the trip as real, and the button leaves it alone.
 */
export const DEMO_MARK = "[demo]";

export function isDemoTrip(t: { notes?: string | null } | null | undefined): boolean {
  const first = String(t?.notes ?? "").split(/\r?\n/, 1)[0] ?? "";
  return first.trim() === DEMO_MARK;
}

/** The ids of the demo trips among these rows, and nothing else. What /api/rentals/demo/clear deletes. */
export const demoTripIds = (rows: ReadonlyArray<{ id: string; notes?: string | null }>): string[] =>
  rows.filter((r) => isUuid(r.id) && isDemoTrip(r)).map((r) => r.id);

/**
 * WHERE ARCHIVE PUTS A TRIP. The same request, "put this away", means two
 * different lanes depending on whether the RV ever left: a trip still in
 * the yard is Cancelled (nothing happened), and one that went out is
 * Closed (it happened, and it is history). Null when it is already put
 * away, so the button is not drawn on a closed or cancelled trip.
 *
 * Archive is not a move. It skips lanes on purpose -- that is the point
 * of it -- so it does not go through mayMove(), and the server makes it
 * conditional on the lane the button was drawn from instead.
 */
export function archiveTo(stage: Stage): "closed" | "cancelled" | null {
  if (stage === "closed" || stage === "cancelled") return null;
  return CANCELLABLE.includes(stage) ? "cancelled" : "closed";
}

/**
 * WHO MAY DELETE A TRIP FOR GOOD. The owner and the supervisor, the same
 * two roles the Reports link is for. Everybody else who can write can
 * Archive, which keeps the history; a delete takes the walk rounds with it
 * and cannot be undone, so it is the office's call.
 */
export const mayDeleteTrip = (role: string | null | undefined): boolean => role === "admin" || role === "supervisor";
